import type { EtapaComercial, StatusReserva } from './statusReserva'
import type { StatusDefesa } from './resumoAdmin'
import type { FormatoReuniao } from './reuniao'
import type { ScoreComercial } from './scoreComercial'
import type { StatusProduto } from './produto'
import type { DadosDeclaradosElegibilidade, RotaOperacao } from './elegibilidade'

export type OportunidadeEstagio = 'DIAGNOSTICO' | 'ESTRUTURACAO' | 'NEGOCIACAO' | 'CONTRATADA'

export type PerfilCredito = 'ASSET_BASED' | 'CASHFLOW_BASED' | 'HIBRIDO' | 'OUTRO'
export type PerfilOperacao = 'ELEGIVEL' | 'ELEGIVEL_COM_RESTRICOES' | 'NAO_ELEGIVEL' | 'EM_ANALISE'

/**
 * Status do subworkflow de PROPOSTA dentro do estágio ESTRUTURACAO.
 *
 * Quando NULL/undefined: proposta ainda não foi iniciada (estado
 * implícito "Aguardando início"). Os demais valores são:
 *  - RASCUNHO         operador montando a proposta
 *  - ENVIADA          proposta enviada para análise/cliente
 *  - AGUARDANDO_DOCS  pausada por falta de docs obrigatórios
 *  - BLOQUEADA        2+ docs faltantes ou pendência crítica
 */
export type PropostaStatus = 'RASCUNHO' | 'ENVIADA' | 'AGUARDANDO_DOCS' | 'BLOQUEADA'

/** Nomes dos campos da estruturação que a IA pode sugerir. */
export type CampoEstruturacao =
  | 'receitaLiquidaAnterior'
  | 'receitaLiquidaVigente'
  | 'resultadoOperacionalAnterior'
  | 'resultadoOperacionalVigente'
  | 'patrimonioLiquido'
  | 'endividamento'
  | 'caixaAtual'
  | 'contasReceber'
  | 'posicionamentoEstrategico'
  | 'presencaGeografica'

/** Contribuição da IA para um campo (origem OCR/extração de documentos). */
export interface SugestaoCampo {
  valor: number | string
  confianca: number
  fonte: string
  documentoId?: string
}

/** Sugestão de estruturação pré-preenchida automaticamente pela IA. */
export interface EstruturacaoSugerida {
  campos: Partial<Record<CampoEstruturacao, SugestaoCampo>>
  atualizadoEm?: string
}

export interface Oportunidade {
  id: string
  /** Número sequencial amigável da reserva (ex.: 1 → "RES-0001"). */
  numero?: number
  clienteId: string
  tipoNecessidade: string
  /**
   * Produto do catálogo. Nulo nas reservas anteriores ao vínculo formal (item 31) e na
   * "outra necessidade" do wizard. Vem na LISTAGEM (`OportunidadeResumoResponse`, desde
   * 8511425 — pedido do R01 do DEV-5) e no detalhe: é contrato, não campo opcional.
   */
  produtoId: string | null
  /**
   * P1 (AN-PP1 §1.6): código do produto lido da projeção (`produtos_projetados`). "Outra
   * necessidade" = `produtoCodigo === 'OTHER_NEED'` OU `produtoId == null` (legado; o backend
   * omite o campo nulo — `non_null`). `produtoCodigo` ausente =
   * backend anterior à V60 ou projeção ainda sem código.
   */
  produtoCodigo?: string | null
  /** P1 (AN-PP1 §1.6): status do catálogo do produto, da projeção. */
  produtoStatus?: StatusProduto | null
  /** P2 (AN-PP2 §5.1, Q67): rota gravada — só produtos `RECEIVABLES_RANGE`; nula nos demais. */
  rotaOperacao?: RotaOperacao | null
  valorEstimado: number
  descricao: string
  estagio: OportunidadeEstagio
  /**
   * O status real (18 estados) — `estagio` é a projeção lossy de 4 estados. Vem na
   * listagem (`OportunidadeResumoResponse`) e no detalhe; opcional só para conviver
   * com respostas antigas.
   */
  status?: StatusReserva | null
  /**
   * Leitura comercial em seis etapas (decisão 6.4), derivada no backend do status +
   * primeiro contato. É o que o consultor vê; o Kanban segue nos 18 status.
   */
  etapaComercial?: EtapaComercial | null
  /**
   * Score Comercial (PDF Pipeline §2.1, decisão 8.1): derivado da etapa pela tabela
   * versionada do backend, calculado na leitura — nunca digitado, nunca calculado aqui.
   * Vem na listagem (classe para o chip do card) e no detalhe (as três dimensões).
   * Ausente = backend anterior à V47.
   */
  scoreComercial?: ScoreComercial | null
  /**
   * "Última movimentação" do card (PDF §2.3): data da última mudança de etapa; sem
   * nenhuma, `updatedAt`. Só na listagem.
   */
  ultimaMovimentacaoEm?: string | null
  /**
   * Controle otimista (V47, PDF §2.4): devolvida em `versaoEsperada` ao transicionar —
   * divergência dá 409 `CONFLITO_VERSAO` e a tela recarrega.
   */
  versao?: number | null
  /** Motivo padronizado da perda (decisão 8.6) — só no detalhe, só em reserva perdida. */
  motivoPerdaCodigo?: string | null
  motivoPerdaObservacao?: string | null
  /**
   * Defesa de Crédito (Onda 4 / A08, V41). Na LISTAGEM chega desde a Onda 6 (A11,
   * lacuna (d)) — a Estruturação Administrativa não precisa mais do detalhe por
   * reserva. Nulo = defesa não iniciada. No DETALHE, o CONSULTOR recebe os textos da
   * minuta zerados (só o estado do fluxo).
   */
  statusDefesa?: StatusDefesa | null
  defesaAprovadaPor?: string | null
  defesaAprovadaEm?: string | null
  /**
   * Percentual (0–100) do checklist de documentos obrigatórios, calculado no backend
   * na listagem (100 quando o produto não exige nada). Ausente = backend anterior.
   */
  percentualChecklist?: number | null
  /** Canal comercial carimbado na criação (6.7). Nulo em reservas antigas. */
  canal?: string | null
  /**
   * Score de aderência (0–100), calculado pelo backend na listagem. Substituiu a
   * probabilidade digitada (decisão 6.3, 16/08) — é o que ordena a fila de Estruturação.
   */
  scoreAderencia?: number | null
  createdAt: string
  updatedAt: string
  nomeCliente?: string

  // Identidade da empresa na própria reserva (modelo reserva-primeiro).
  // Presentes quando a reserva carrega os dados do cliente; podem faltar em
  // reservas antigas, caso em que a tela faz fallback para o cadastro de cliente.
  cnpj?: string | null
  razaoSocial?: string | null
  nomeFantasia?: string | null
  /** Consultor responsável (id do usuário no Keycloak). */
  usuarioResponsavel?: string | null
  /**
   * Nome do consultor responsável gravado na reserva (V49, 14/10/2026): na criação e na
   * transferência de carteira. Nulo em reserva antiga sem nome conhecido.
   */
  consultorNome?: string | null
  /** Prazo de fechamento (ISO YYYY-MM-DD); base do alerta de vencimento pré-contratação. */
  prazoReserva?: string | null
  /** Data da assinatura do contrato — origem do prazo de operacionalização. */
  dataAssinatura?: string | null
  /**
   * Segundo prazo de 60 dias: assinatura + 60 dias para operacionalizar.
   * Só existe depois de CONTRATO_ASSINADO.
   */
  prazoOperacionalizacao?: string | null
  /**
   * Nulo = a renovação automática (+30, única — Handoff v4.0) ainda está disponível.
   * Decide o modo do diálogo de renovação do consultor.
   */
  renovacaoAutomaticaUsadaEm?: string | null

  // Parceiro escolhido pela Originei (seleção manual, sem ranking — Handoff v4.0).
  // É o ÚNICO dado de parceiro que o consultor vê.
  parceiroSelecionadoId?: string | null
  parceiroSelecionadoNome?: string | null
  parceiroSelecionadoEm?: string | null

  /**
   * "O que fazer a seguir" (Fase 6, item 18) — lembrete de trabalho persistido, não status.
   * Aparece no card do Kanban e na lista; vem também no resumo da listagem.
   */
  proximaAcao?: string | null
  proximaAcaoAtualizadaEm?: string | null

  // Estruturação da reserva
  posicionamentoEstrategico?: string | null
  presencaGeografica?: string | null
  receitaLiquidaAnterior?: number | null
  receitaLiquidaVigente?: number | null
  resultadoOperacionalAnterior?: number | null
  resultadoOperacionalVigente?: number | null
  patrimonioLiquido?: number | null
  endividamento?: number | null
  caixaAtual?: number | null
  contasReceber?: number | null
  estruturadaEm?: string | null
  estruturadaPor?: string | null
  /** Sugestão da IA (OCR) para pré-preencher a estruturação. */
  estruturacaoSugerida?: EstruturacaoSugerida | null

  // Proposta (subworkflow do estágio ESTRUTURACAO)
  propostaStatus?: PropostaStatus | null
  propostaIniciadaEm?: string | null
  propostaEnviadaEm?: string | null
  propostaAtualizadaPor?: string | null

  sacadoId?: string | null
  dataVencimento?: string | null
  perfilCredito?: PerfilCredito | null
  analiseFinanceira?: string | null
  teseCreditoFundamento?: string | null
  avaliacaoCedente?: string | null
  recomendacao?: string | null
  perfilOperacao?: PerfilOperacao | null
  pontosPositivos?: string[]
  pontosAtencao?: string[]

  /**
   * Checklist de documentos obrigatórios, calculado no backend — vem na LISTAGEM
   * (`OportunidadeResumoResponse`). Ausente = backend anterior.
   */
  documentosObrigatoriosFaltantes?: number | null

  // ─── Solicitação de reunião pelo back-office (entrega G, V50) ───
  // Na listagem chegam `reuniaoSolicitadaEm`, `reuniaoSolicitadaObservacao` e
  // `dataPrimeiroContato`; quem pediu (id e nome) só no detalhe.
  /** Quando o back-office pediu a reunião ao consultor; nulo = não solicitada. */
  reuniaoSolicitadaEm?: string | null
  reuniaoSolicitadaPor?: string | null
  reuniaoSolicitadaPorNome?: string | null
  /** Recado do back-office ao consultor junto do pedido. */
  reuniaoSolicitadaObservacao?: string | null
  /** Primeiro contato (reunião realizada), YYYY-MM-DD; nulo = ainda não houve. */
  dataPrimeiroContato?: string | null

  // ─── Análise da Originei (entrega H, V51) — texto livre, uso interno ───
  // Só no detalhe (GET /{id}), nunca na listagem; SEMPRE nulo para o CONSULTOR.
  analiseOriginei?: string | null
  analiseOrigineiAtualizadaEm?: string | null
  analiseOrigineiAtualizadaPor?: string | null
}

/** Corpo do `POST /v1/oportunidades/{id}/solicitacao-reuniao` (observação até 2000). */
export interface SolicitarReuniaoRequest {
  observacao?: string | null
}

/** Ciclo de vida do encaminhamento ao parceiro (Épico 4). */
export type StatusEncaminhamento = 'ENVIADO' | 'ACEITO' | 'RECUSADO'

/**
 * Encaminhamento formal da reserva ao parceiro (Épico 4). O pacote é um snapshot JSON
 * imutável do que foi enviado; o hash (SHA-256) e o protocolo `ENC-{numero}-{versao}`
 * são a identidade e a prova. Reenvio incrementa a versão — nada é sobrescrito.
 */
export interface EncaminhamentoParceiro {
  id: string
  oportunidadeId: string
  parceiroId: string
  parceiroNome?: string | null
  protocolo: string
  versao: number
  pacote?: string | null
  pacoteHash: string
  status: StatusEncaminhamento
  enviadoPor?: string | null
  enviadoEm: string
  retornoMotivo?: string | null
  retornoEm?: string | null
}

export interface DefesaCreditoRequest {
  sacadoId?: string | null
  dataVencimento?: string | null
  perfilCredito?: PerfilCredito | null
  analiseFinanceira?: string | null
  teseCreditoFundamento?: string | null
  avaliacaoCedente?: string | null
  recomendacao?: string | null
  perfilOperacao?: PerfilOperacao | null
  pontosPositivos?: string[]
  pontosAtencao?: string[]
}

/**
 * Carteirização: move a reserva para a carteira de outro consultor.
 * Ação de gestão (ADMIN/GERENTE) — não do dono da reserva.
 */
export interface TransferirReservaRequest {
  novoConsultorId: string
  motivo: string
  /** Nome do novo consultor, gravado na reserva (V49). Opcional. */
  novoConsultorNome?: string | null
}

/** Consultor com reserva no escopo de quem pede — opção do filtro "Consultor" (14/10/2026). */
export interface ConsultorReserva {
  id: string
  /** Nome gravado nas reservas; nulo quando nenhuma o tem. */
  nome: string | null
}

export interface TransferenciaReserva {
  id: string
  oportunidadeId: string
  /** Nulo em reserva legada que não tinha responsável. */
  consultorAnterior?: string | null
  consultorNovo: string
  motivo: string
  transferidoPor?: string | null
  transferidoEm: string
}

/**
 * Renovação (extensão de prazo) da reserva. Ação do lado Originei
 * (ADMIN/GERENTE/OPERADOR) — o consultor solicita por fora deste fluxo.
 * `novoPrazo` é ISO YYYY-MM-DD e precisa ser posterior ao prazo atual.
 */
export interface RenovarReservaRequest {
  novoPrazo: string
  motivo?: string
  usuarioId: string
}

export interface OportunidadeRequest {
  clienteId: string
  tipoNecessidade: string
  /** Produto do catálogo. Nulo nas reservas anteriores ao vínculo formal (item 31). */
  produtoId?: string | null
  valorEstimado: number
  descricao: string
  estagio: OportunidadeEstagio
}

export interface OportunidadeUpdateRequest extends OportunidadeRequest {}

/**
 * Reunião de primeiro contato criada JUNTO com a reserva (Onda 3, bloco
 * "3. Agendar com o Comercial Originei"). Espelha `dto/ReuniaoInicialRequest`.
 */
export interface ReuniaoInicialRequest {
  /** 'YYYY-MM-DDTHH:mm:ss' local (LocalDateTime no backend — sem fuso/Z). */
  dataHora: string
  formato: FormatoReuniao
  /** Obrigatório quando formato = PRESENCIAL (LOCAL_OBRIGATORIO_PRESENCIAL). */
  local?: string | null
  observacoes?: string | null
}

/**
 * Corpo do `POST /v1/oportunidades` da Onda 3 — espelha `dto/CriarOportunidadeRequest`
 * (superconjunto do `OportunidadeRequest`, modelo reserva-primeiro: CNPJ OU clienteId).
 * Com `reuniaoInicial`, reserva e reunião nascem na MESMA transação — em qualquer erro
 * (RESERVA_DUPLICADA, REUNIAO_INICIAL_INVALIDA, LOCAL_OBRIGATORIO_PRESENCIAL) nada é criado.
 */
export interface CriarOportunidadeRequest {
  clienteId?: string | null
  cnpj?: string | null
  razaoSocial?: string | null
  nomeFantasia?: string | null
  tipoNecessidade: string
  produtoId?: string | null
  valorEstimado: number
  descricao?: string | null
  notas?: string | null
  reuniaoInicial?: ReuniaoInicialRequest | null
  /**
   * "Registrar reserva" em nome de um consultor (Onda 6 / A11): SÓ ADMIN/GERENTE — o
   * backend responde 403 para qualquer outro papel que envie `consultorId`. A reserva
   * (e a reunião inicial) nascem do consultor escolhido; quem registrou fica na
   * atividade/auditoria (`origemRegistro=ADMIN`). Ausente = fluxo próprio do consultor.
   */
  consultorId?: string | null
  /** Nome exibido do consultor escolhido (para a reunião inicial); opcional. */
  consultorNome?: string | null
  /**
   * Respostas às perguntas de elegibilidade (09/09), gravadas na reserva na mesma transação
   * da criação. Texto livre é avaliado manualmente pela Originei na validação da reserva.
   */
  respostasElegibilidade?: RespostaElegibilidadeRequest[] | null
  /**
   * P2 (AN-PP2 §8, Q69): dados declarados para a elegibilidade — faturamento anual e, no
   * Home Equity, imóvel/fonte/valor solicitado. O servidor avalia, grava o snapshot `CRIACAO` e
   * NUNCA recusa a criação por elegibilidade (Q66). Substitui o "Faturamento informado" das notas.
   */
  dadosElegibilidade?: DadosDeclaradosElegibilidade | null
  /**
   * P5 (AN-PP5 §3.6): "Nova operação (renovação de #N)" — a reserva anterior (ATIVO/ENCERRADA,
   * mesmo CNPJ e produto, da carteira). Só rastreabilidade: nada é herdado. Inválida → 422
   * `OPERACAO_ANTERIOR_INVALIDA`. Ausente = reserva comum (o campo nem vai no corpo).
   */
  operacaoAnteriorId?: string | null
}

/**
 * P5 (AN-PP5 §3.6): candidata a "operação anterior" — `GET /api/v1/oportunidades/operacoes-anteriores`.
 * O backend já filtra (mesmo CNPJ e produto, ATIVO/ENCERRADA, escopo de quem pede); a tela só lista.
 */
export interface OperacaoAnteriorCandidata {
  id: string
  numero?: number | null
  status: StatusReserva
  razaoSocial?: string | null
  produtoId?: string | null
  usuarioResponsavel?: string | null
  dataAssinatura?: string | null
}

/** Situação da avaliação humana de uma resposta (só texto livre passa por ATENDE/NAO_ATENDE). */
export type AvaliacaoManual = 'NAO_SE_APLICA' | 'PENDENTE' | 'ATENDE' | 'NAO_ATENDE'

export const AVALIACAO_MANUAL_LABEL: Record<AvaliacaoManual, string> = {
  NAO_SE_APLICA: 'Avaliada pelo motor',
  PENDENTE: 'Aguardando avaliação',
  ATENDE: 'Atende',
  NAO_ATENDE: 'Não atende',
}

/** Uma resposta como o wizard a envia — espelha `dto/RespostaElegibilidadeRequest`. */
export interface RespostaElegibilidadeRequest {
  perguntaId: string
  enunciado: string
  tipoResposta: string
  resposta: string | null
  ordem?: number
}

/** Uma resposta gravada na reserva — espelha `dto/RespostaElegibilidadeResponse`. */
export interface RespostaElegibilidadeReserva {
  id: string
  oportunidadeId: string
  perguntaId: string
  ordem: number
  enunciado: string
  tipoResposta: string
  resposta: string | null
  avaliacaoManual: AvaliacaoManual
  avaliacaoObservacao?: string | null
  avaliadoPor?: string | null
  avaliadoEm?: string | null
}

export interface AvaliacaoRespostaRequest {
  resultado: AvaliacaoManual
  observacao?: string | null
}

/**
 * Score de aderência calculado — espelha `dto/ScoreAderenciaResponse`.
 *
 * O contrato de honestidade do número: a soma dos componentes É o score, e cada evidência
 * nomeia o que falta para subir. Desde 16/08 (decisão 6.3) é o ÚNICO score da reserva —
 * a probabilidade digitada foi removida por nunca ter sido um julgamento real (nascia 30
 * fixo para toda reserva).
 */
export interface ScoreAderencia {
  oportunidadeId: string
  score: number
  maximo: number
  componentes: ComponenteScore[]
}

export interface ComponenteScore {
  nome: string
  pontos: number
  maximo: number
  evidencia: string
}
